import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, updateProfile, sendPasswordResetEmail } from "firebase/auth";
import Modal from "../components/Modal"; // Importuj Modal komponentu
import { useTranslation } from "react-i18next";
import { getPushStatus, subscribeToPushNotifications, unsubscribeFromPushNotifications, sendTestPushNotification,} from "../utils/pushNotifications";

const UserProfile = () => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState([]);
  const [userData, setUserData] = useState(null);
  const [updatedName, setUpdatedName] = useState("");
  const [updatedPhoto, setUpdatedPhoto] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false); // State za prikazivanje modala
  const [modalMessage, setModalMessage] = useState(""); // Poruka koju prikazujemo u modalu
  const [modalTitle, setModalTitle] = useState(""); // Naslov modala
  const [modalConfirmText, setModalConfirmText] = useState(""); // Tekst na dugmetu potvrde
  const [modalCancelText, setModalCancelText] = useState(""); // Tekst na dugmetu otkazivanja
  const [availableRoles, setAvailableRoles] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [pushStatus, setPushStatus] = useState({
  supported: true,
  permission: "default",
  subscribed: false,
  endpoint: null,
  });
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState("");

  const loadPushStatus = async () => {
  try {
    const status = await getPushStatus();
    setPushStatus(status);
  } catch (err) {
    console.error("❌ Failed to load push status:", err);
  }
  };


  const auth = getAuth();

  // Pričekaj da Firebase potvrdi ko je korisnik
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }
      fetchUser(firebaseUser);
    });

    return unsubscribe; // Očisti listener
  }, []);

  useEffect(() => {
  if (userData?.roles?.includes("admin")) {
    fetchAllUsers();
  }
}, [userData]);


  const fetchAllUsers = async () => {
  try {
    const token = await auth.currentUser.getIdToken();

    const res = await fetch("https://modularobjects.com/checkout/api/user/all", {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    setAllUsers(data);
  } catch (err) {
    console.error("❌ Failed to load all users:", err);
  }
};


  // Učitaj podatke s back-enda
  const fetchUser = async (firebaseUser) => {
  try {
    const res = await fetch(`https://modularobjects.com/checkout/api/user/firebase-user/${firebaseUser.uid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!res.ok) throw new Error(`Server error: ${res.status}`);
    const data = await res.json();

    setUserData(data);
    fetchSubscription(firebaseUser.uid);
    loadPushStatus();
    setUpdatedName(data.display_name ?? firebaseUser.displayName ?? "");
    setUpdatedPhoto(data.photo_url ?? firebaseUser.photoURL ?? "");

    // Ažuriraj subscription status
    setSubscriptionStatus(data.subscription_status || "No active subscription");

  } catch (err) {
    console.error("❌ Fetch user failed", err);
    setModalTitle(t("error"));
    setModalMessage(t("login.fetch_user_failed"));
    setModalConfirmText(t("close"));
    setShowModal(true);
  } finally {
    setLoading(false);
  }
};

const handleEnablePush = async () => {
  try {
    setPushLoading(true);
    setPushMessage("");
    await subscribeToPushNotifications();
    await loadPushStatus();
    setPushMessage("Push notifications enabled.");
  } catch (err) {
    console.error("❌ Enable push failed:", err);
    setPushMessage(err.message || "Failed to enable push notifications.");
  } finally {
    setPushLoading(false);
  }
};

const handleDisablePush = async () => {
  try {
    setPushLoading(true);
    setPushMessage("");
    await unsubscribeFromPushNotifications();
    await loadPushStatus();
    setPushMessage("Push notifications disabled.");
  } catch (err) {
    console.error("❌ Disable push failed:", err);
    setPushMessage(err.message || "Failed to disable push notifications.");
  } finally {
    setPushLoading(false);
  }
};

const handleTestPush = async () => {
  try {
    setPushLoading(true);
    setPushMessage("");
    await sendTestPushNotification();
    setPushMessage("Test notification sent.");
  } catch (err) {
    console.error("❌ Test push failed:", err);
    setPushMessage(err.message || "Failed to send test push.");
  } finally {
    setPushLoading(false);
  }
};

const showPushTestTools =
  process.env.REACT_APP_ENABLE_NOTIFICATION_TESTS === "true" ||
  userData?.roles?.includes("admin");
  

  useEffect(() => {
  const fetchRoles = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://modularobjects.com/checkout/api/user/roles", {
  headers: { Authorization: `Bearer ${token}` }
});
const roleNames = await res.json();
setAvailableRoles(roleNames);
    } catch (err) {
      console.error("❌ Failed to load available roles:", err);
    }
  };

  if (userData?.roles?.includes("admin")) {
    fetchRoles(); // učitaj role kada znamo da je admin
  }
}, [userData]);

  // Spremi izmene
  const handleUpdate = async () => {
    if (!userData) return;
    setUpdating(true);
    try {
      await updateProfile(auth.currentUser, {
        displayName: updatedName,
        photoURL: updatedPhoto,
      });

      await fetch(`https://modularobjects.com/checkout/api/user/firebase-user/${userData.uid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: updatedName,
          photoURL: updatedPhoto,
        }),
      });

      setUserData(prev => ({
        ...prev,
        display_name: updatedName,
        photo_url: updatedPhoto,
      }));

      setModalTitle(t("success"));
      setModalMessage(t("login.profile_updated"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    } catch (err) {
      console.error("❌ Update error:", err);
      setModalTitle(t("error"));
      setModalMessage(t("login.update_failed"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleRoleChange = (userId, newRoles) => {
  setAllUsers((prev) =>
    prev.map((u) => (u.id === userId ? { ...u, roles: newRoles } : u))
  );
};

const handleSaveRoles = async (userId, roles) => {
  try {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`https://modularobjects.com/checkout/api/user/${userId}/roles`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ roles })
    });

    if (!res.ok) throw new Error("Failed to save roles");
    alert("✅ Roles updated");
  } catch (err) {
    console.error("❌ Save roles failed", err);
    alert("❌ Failed to update roles");
    console.log("📤 Sending roles:", roles);
console.log("📤 Sending to userId:", userId);
  }
};

const fetchSubscription = async (uid) => {
  try {
    setSubscriptionData(null); // 🔄 Resetuj prethodne podatke ako treba
    const token = await auth.currentUser.getIdToken();

    const res = await fetch(`https://modularobjects.com/checkout/api/user/subscription/${uid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error("Failed to fetch subscription");

    const data = await res.json();
    setSubscriptionData(data);
    console.log("✅ Subscription fetched:", data); // 🔍 Debug
  } catch (err) {
    console.error("❌ Subscription fetch error:", err);
  }
};





const handleChangeSubscriptionPlan = async () => {
  if (!userData) return;

  // Novi Price ID za paket
  if (!selectedPlan) {
  alert("Please select a new plan");
  return;
}
const newPriceId = selectedPlan;

  try {
    const res = await fetch("https://modularobjects.com/checkout/api/subscription/change-plan", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: userData.uid,
        newPriceId: newPriceId,
      }),
    });

    const data = await res.json();

    if (data.success) {
      setModalTitle(t("success"));
      setModalMessage(t("subscription.plan_updated"));
      setModalConfirmText(t("close"));
      setShowModal(true);
      // Ažuriraj status na frontend-u
      setSubscriptionStatus("Updated");
    } else {
      setModalTitle(t("error"));
      setModalMessage(t("subscription.update_failed"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    }
  } catch (err) {
    console.error("❌ Error updating subscription", err);
    setModalTitle(t("error"));
    setModalMessage(t("subscription.update_failed"));
    setModalConfirmText(t("close"));
    setShowModal(true);
  }
};

const handleCancelSubscription = async () => {
  if (!userData) return;

  try {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken(); // 🔑 preuzmi Firebase ID token

    const res = await fetch("https://modularobjects.com/checkout/api/subscription/cancel", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // ✅ dodato!
      },
      body: JSON.stringify({
        userId: userData.uid,
      }),
    });

    const data = await res.json();

    if (data.success) {
      setModalTitle(t("success"));
      setModalMessage(t("subscription.cancelled"));
      setModalConfirmText(t("close"));
      setShowModal(true);
      setSubscriptionStatus("Cancelled");
    } else {
      setModalTitle(t("error"));
      setModalMessage(t("subscription.cancel_failed"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    }
  } catch (err) {
    console.error("❌ Error canceling subscription", err);
    setModalTitle(t("error"));
    setModalMessage(t("subscription.cancel_failed"));
    setModalConfirmText(t("close"));
    setShowModal(true);
  }
};



useEffect(() => {
  const fetchPlans = async () => {
    try {
      const res = await fetch("https://modularobjects.com/checkout/api/user/plans");
      const data = await res.json();
      setPlans(data);
    } catch (err) {
      console.error("❌ Failed to load plans", err);
    }
  };

  fetchPlans();
}, []);

const handlePlanClick = async (newPriceId) => {
  try {
    const token = await auth.currentUser.getIdToken(); // Firebase token
    const res = await fetch("https://modularobjects.com/checkout/api/subscription/change-plan", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}` // ako koristite verifyToken middleware
      },
      body: JSON.stringify({
        userId: userData.uid,
        newPriceId
      })
    });

    const data = await res.json();
    if (res.ok && data.success) {
      alert("✅ Plan successfully changed");
      await fetchSubscription(userData.uid);
    } else {
      throw new Error(data?.error || "Unknown error");
    }
  } catch (err) {
    console.error("❌ Failed to change plan:", err);
    alert("❌ Error changing plan");
  }
};





  // Reset lozinke
  const handlePasswordReset = async () => {
    if (!userData?.email) return alert(t("login.no_email_for_reset"));
    try {
      await sendPasswordResetEmail(auth, userData.email);
      setModalTitle(t("success"));
      setModalMessage(t("login.password_reset_link_sent"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    } catch (err) {
      console.error("❌ Password reset error:", err);
      setModalTitle(t("error"));
      setModalMessage(t("login.password_reset_failed"));
      setModalConfirmText(t("close"));
      setShowModal(true);
    }
  };

  if (loading) return <p>⏳ {t("loading_profile")}</p>;

  return (
    <div className="max-w-xl mx-auto p-6 bg-white dark:bg-neutral-800 rounded shadow">
      <h2 className="text-2xl font-bold mb-6 text-center">👤 {t("user_profile")}</h2>

      <label className="block mb-1 text-sm font-medium">{t("email")}</label>
      <input
        type="email"
        value={userData?.email || ""}
        disabled
        className="w-full border px-3 py-2 rounded mb-4 bg-gray-200 text-gray-600 dark:bg-neutral-700"
      />

      <label className="block mb-1 text-sm font-medium">{t("name")}</label>
      <input
        type="text"
        value={updatedName}
        onChange={(e) => setUpdatedName(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-4 bg-gray-100 dark:bg-neutral-700"
      />

      <label className="block mb-1 text-sm font-medium">{t("photo_url")}</label>
      <input
        type="text"
        value={updatedPhoto}
        onChange={(e) => setUpdatedPhoto(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-4 bg-gray-100 dark:bg-neutral-700"
      />

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <button
          onClick={handleUpdate}
          className="btn-primary w-full"
        >
          💾 {t("save_changes")}
        </button>

        <button
          onClick={handlePasswordReset}
          className="btn-primary w-full"
        >
          🔐 {t("change_password")}
        </button>
        
      </div>

      <div className="mt-6 p-4 bg-blue-50 dark:bg-neutral-700 rounded border dark:border-neutral-600">
  <h3 className="text-lg font-bold mb-3">🔔 Push Notifications</h3>

  <div className="space-y-2 text-sm">
    <p>
      <strong>Browser permission:</strong> {pushStatus.permission}
    </p>
    <p>
      <strong>Subscription:</strong> {pushStatus.subscribed ? "Active" : "Inactive"}
    </p>
    <p>
      <strong>Support:</strong> {pushStatus.supported ? "Supported" : "Not supported"}
    </p>
  </div>

  <div className="flex flex-col md:flex-row gap-3 mt-4">
    <button
  onClick={pushStatus.subscribed ? handleDisablePush : handleEnablePush}
  disabled={pushLoading}
  className={`px-4 py-2 rounded text-white ${
    pushStatus.subscribed
      ? "bg-red-500 hover:bg-red-600"
      : "bg-green-600 hover:bg-green-700"
  }`}
>
  {pushLoading
    ? "Working..."
    : pushStatus.subscribed
    ? "🔕 Disable Push"
    : "🔔 Enable Push"}
</button>

    {showPushTestTools && (
      <button
        onClick={handleTestPush}
        disabled={pushLoading}
        className="btn-primary"
      >
        Send Test Push
      </button>
    )}
  </div>

  {pushMessage ? (
    <p className="mt-3 text-sm text-gray-700 dark:text-gray-200">
      {pushMessage}
    </p>
  ) : null}
</div>

      
{subscriptionData && (
  <div className="mt-6 p-4 bg-blue-50 dark:bg-neutral-700 rounded border dark:border-neutral-600">
    <div className="flex flex-col md:flex-row justify-between gap-6">
      
      {/* Leva strana - info */}
      <div className="flex-1">
        <h3 className="text-lg font-bold mb-2">{t("subscription.status")}</h3>
        <p>{t("subscription.current_status")}: <strong>{subscriptionData.status}</strong></p>
        <p>{t("subscription.plan")}: {subscriptionData.plan_name || "N/A"}</p>
        <p>{t("subscription.next_billing")}: {new Date(subscriptionData.valid_until).toLocaleDateString()}</p>
        <p>{t("subscription.amount")}: ${(subscriptionData.amount / 100).toFixed(2)} {subscriptionData.currency?.toUpperCase()}</p>
        <p>{t("subscription.card")}: **** **** **** {subscriptionData.payment_method_last4}</p>
        <a
          href={subscriptionData.receipt_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {t("subscription.view_invoice")}
        </a>

        {subscriptionData.status === "active" && (
          <div className="mt-4">
            <button onClick={handleCancelSubscription} className="btn-danger">
              {t("subscription.cancel")}
            </button>
          </div>
        )}
      </div>

      {/* Desna strana - izbor plana */}
      {plans.length > 0 && (
        <div className="flex-1 md:max-w-xs">
          <h4 className="font-semibold mb-2">{t("subscription.choose_plan")}</h4>
          <div className="flex flex-col gap-2">
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => handlePlanClick(plan.id)}
                className={`px-3 py-2 rounded text-sm font-semibold border text-center ${
                  subscriptionData.price_id === plan.id
                    ? "bg-primary-light text-white"
                    : "bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100"
                }`}
              >
                {plan.name} – ${plan.amount} {plan.currency}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
)}




{userData?.roles?.includes("admin") && (
  <div className="mt-10">

<div className="mb-6">
  <a
    href="/marketing/admin/projects"
    className="inline-flex items-center gap-2 bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90"
  >
    🛠 Marketing Projects Admin
  </a>
</div>


  <div className="overflow-auto rounded shadow-sm border dark:border-neutral-600">
    <div className="mt-10 space-y-4">
   <h3 className="text-xl font-bold mb-4">👥 {t("admin.all_users")}</h3>
  {allUsers.map((u) => (
    <div
      key={u.id}
      className="p-4 border rounded-lg bg-white dark:bg-neutral-800 shadow flex flex-col gap-2"
    >
      <div className="flex flex-col sm:flex-row justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">📧 {u.email}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">👤 {u.display_name}</p>
        </div>

        <div className="mt-2 sm:mt-0 flex items-center gap-2 flex-wrap">
          <select
  multiple
  value={u.roles}
  onChange={(e) => {
    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
    handleRoleChange(u.id, selected);
  }}
  className="border rounded px-2 py-1 text-sm bg-white dark:bg-neutral-700"
>
  {availableRoles.map((r) => (
    <option key={r} value={r}>{r}</option>
  ))}
</select>
          <button
            className="btn-primary px-3 py-1 text-sm"
            onClick={() => handleSaveRoles(u.id, u.roles)}
          >
            💾 Save
          </button>
        </div>
      </div>
    </div>
  ))}
</div>
  </div>
</div>
)}


      {/* Modal za prikaz poruka */}
      {showModal && (
        <Modal
          title={modalTitle}
          message={modalMessage}
          onClose={() => setShowModal(false)}
          onConfirm={() => setShowModal(false)}
          confirmText={modalConfirmText}
          cancelText={modalCancelText}
        />
      )}
    </div>
  );
};

export default UserProfile;
